// ============================================
// HELPER FUNCTIONS
// ============================================

export const getCountryFlag = (country) => {
  const flags = {
    'Canada': '🇨🇦', 'USA': '🇺🇸', 'UK': '🇬🇧', 'Australia': '🇦🇺',
    'Germany': '🇩🇪', 'France': '🇫🇷', 'Spain': '🇪🇸', 'Italy': '🇮🇹',
    'Netherlands': '🇳🇱', 'Portugal': '🇵🇹', 'Ireland': '🇮🇪', 'New Zealand': '🇳🇿'
  };
  return flags[country] || '🌍';
};

// Generate shareable content
export const generateShareContent = (format = 'html', data, shareSections, hasProfileData, profileData) => {
    if (!data) return '';
    
    const { summary, assessment, pathways, blockers, risks, actionItems, missingInformation, nextSteps, internalNotes, professionalAdvice, firmStandardsCompliance } = data;
    
    let content = '';
    
    if (format === 'html') {
      content = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Case Analysis Report</title>
  <style>
    body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; background: #1a1f2e; color: #e8eaed; }
    h1 { color: #a5b4fc; border-bottom: 2px solid #6366f1; padding-bottom: 10px; }
    h2 { color: #86efac; margin-top: 24px; }
    h3 { color: #fbbf24; }
    .section { background: rgba(99,102,241,0.1); padding: 16px; border-radius: 8px; margin: 12px 0; border-left: 4px solid #6366f1; }
    .stat { display: inline-block; background: rgba(99,102,241,0.2); padding: 8px 16px; border-radius: 20px; margin: 4px; }
    ul { padding-left: 20px; }
    li { margin: 8px 0; }
    .blocker { color: #f87171; }
    .risk { color: #fbbf24; }
    .step { background: rgba(34,197,94,0.1); padding: 8px 12px; border-radius: 6px; margin: 6px 0; }
    .footer { margin-top: 30px; padding-top: 20px; border-top: 1px solid #374151; color: #9ca3af; font-size: 12px; }
  </style>
</head>
<body>
  <h1>📋 Case Analysis Report</h1>
  <p style="color: #9ca3af;">Generated: ${new Date().toLocaleString()}</p>`;

      if (shareSections.clientProfile && hasProfileData) {
        content += `
  <div class="section">
    <h2>👤 Client Profile</h2>
    <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px;">
    ${profileData.fullName || profileData.firstName ? `<p><strong>Name:</strong> ${profileData.fullName || `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim()}</p>` : ''}
    ${profileData.email ? `<p><strong>Email:</strong> ${profileData.email}</p>` : ''}
    ${profileData.phone ? `<p><strong>Phone:</strong> ${profileData.phone}</p>` : ''}
    ${profileData.nationality ? `<p><strong>Nationality:</strong> ${profileData.nationality}</p>` : ''}
    ${profileData.dateOfBirth ? `<p><strong>Date of Birth:</strong> ${profileData.dateOfBirth}</p>` : ''}
    ${profileData.maritalStatus ? `<p><strong>Marital Status:</strong> ${profileData.maritalStatus}</p>` : ''}
    ${profileData.occupation ? `<p><strong>Occupation:</strong> ${profileData.occupation}</p>` : ''}
    ${profileData.currentLocation ? `<p><strong>Location:</strong> ${profileData.currentLocation}</p>` : ''}
    ${profileData.targetCountry ? `<p><strong>Target Country:</strong> ${profileData.targetCountry}</p>` : ''}
    ${profileData.timeline ? `<p><strong>Timeline:</strong> ${profileData.timeline}</p>` : ''}
    ${profileData.currentStatus ? `<p><strong>Status:</strong> ${profileData.currentStatus}</p>` : ''}
    ${profileData.canEnglish ? `<p><strong>English:</strong> ${profileData.canEnglish}</p>` : ''}
    ${profileData.canFrench ? `<p><strong>French:</strong> ${profileData.canFrench}</p>` : ''}
    </div>
  </div>`;
      }

      if (shareSections.clientSummary && summary) {
        content += `
  <div class="section">
    <h2>📋 Client Summary</h2>
    ${summary.clientName ? `<p><strong>Client:</strong> ${summary.clientName}</p>` : ''}
    ${summary.category ? `<p><strong>Category:</strong> ${summary.category}</p>` : ''}
    ${summary.objective ? `<p><strong>Objective:</strong> ${summary.objective}</p>` : ''}
    ${summary.context ? `<p><strong>Context:</strong> ${summary.context}</p>` : ''}
    ${summary.nationality ? `<p><strong>Nationality:</strong> ${summary.nationality}</p>` : ''}
    ${summary.currentStatus ? `<p><strong>Status:</strong> ${summary.currentStatus}</p>` : ''}
    ${summary.timeline ? `<p><strong>Timeline:</strong> ${summary.timeline}</p>` : ''}
  </div>`;
      }

      if (shareSections.assessment && assessment) {
        content += `
  <div class="section">
    <h2>📊 Assessment</h2>
    <p><span class="stat">Readiness: ${assessment.overallReadiness}%</span>
    <span class="stat">Confidence: ${assessment.overallConfidence}</span>
    <span class="stat">Complexity: ${assessment.complexityLevel || 'N/A'}</span></p>
  </div>`;
      }

      if (shareSections.blockers && blockers?.length > 0) {
        content += `
  <div class="section">
    <h2>🚫 Blockers</h2>
    <ul>${blockers.map(b => `<li class="blocker">${b}</li>`).join('')}</ul>
  </div>`;
      }

      if (shareSections.risks && risks?.length > 0) {
        content += `
  <div class="section">
    <h2>⚠️ Risks</h2>
    <ul>${risks.map(r => `<li class="risk">${r}</li>`).join('')}</ul>
  </div>`;
      }

      if (shareSections.pathways && pathways?.length > 0) {
        content += `
  <div class="section">
    <h2>🛤️ Recommended Pathways</h2>
    ${pathways.map(p => `
    <h3>${p.name} (Score: ${p.score}%)</h3>
    <p>${p.description}</p>
    <p><strong>Timeline:</strong> ${p.timeline}</p>
    ${p.pros?.length ? `<p><strong>Pros:</strong> ${p.pros.join(', ')}</p>` : ''}
    ${p.cons?.length ? `<p><strong>Cons:</strong> ${p.cons.join(', ')}</p>` : ''}
    `).join('<hr style="border-color: #374151;">')}
  </div>`;
      }

      if (shareSections.missingInfo && missingInformation?.length > 0) {
        content += `
  <div class="section">
    <h2>📋 Missing Information</h2>
    <ul>${missingInformation.map(m => `<li><strong>${m.item}</strong> (${m.priority})${m.question ? ` - "${m.question}"` : ''}</li>`).join('')}</ul>
  </div>`;
      }

      if (shareSections.nextSteps && nextSteps?.length > 0) {
        content += `
  <div class="section">
    <h2>✅ Next Steps</h2>
    ${nextSteps.map((s, i) => `<div class="step"><strong>Step ${i + 1}:</strong> ${s.description || s.step} ${s.timeframe ? `(${s.timeframe})` : ''} ${s.owner ? `- ${s.owner}` : ''}</div>`).join('')}
  </div>`;
      }

      if (shareSections.actionItems && actionItems?.length > 0) {
        content += `
  <div class="section">
    <h2>📌 Action Items</h2>
    <ul>${actionItems.map(a => `<li><strong>${a.action}</strong> - Priority: ${a.priority}${a.deadline ? `, Due: ${a.deadline}` : ''}</li>`).join('')}</ul>
  </div>`;
      }

      if (shareSections.professionalAdvice && data.professionalAdvice) {
        const advice = data.professionalAdvice;
        content += `
  <div class="section">
    <h2>💼 Professional Advice</h2>
    ${advice.expertAnalysis ? `<p><strong>Expert Analysis:</strong> ${advice.expertAnalysis}</p>` : ''}
    ${advice.strategicRecommendations?.length ? `<h3>Strategic Recommendations</h3><ul>${advice.strategicRecommendations.map(r => `<li>${r}</li>`).join('')}</ul>` : ''}
    ${advice.riskConsiderations ? `<p><strong>Risk Considerations:</strong> ${advice.riskConsiderations}</p>` : ''}
    ${advice.longTermPerspective ? `<p><strong>Long-term Perspective:</strong> ${advice.longTermPerspective}</p>` : ''}
  </div>`;
      }

      if (shareSections.internalNotes && internalNotes?.length > 0) {
        content += `
  <div class="section" style="border-left-color: #f59e0b;">
    <h2>🔒 Internal Notes (Confidential)</h2>
    <ul>${internalNotes.map(n => `<li>${n}</li>`).join('')}</ul>
  </div>`;
      }

      if (shareSections.firmCompliance && firmStandardsCompliance?.sections?.length > 0) {
        content += `
  <div class="section">
    <h2>🛡️ Firm Standards Compliance</h2>
    <ul>${firmStandardsCompliance.sections.map(s => `<li><strong>${s.section}:</strong> ${s.status} - ${s.finding || 'N/A'}</li>`).join('')}</ul>
  </div>`;
      }

      content += `
  <div class="footer">
    <p>This report was generated by Maven Platform. For internal use only.</p>
  </div>
</body>
</html>`;
    } else {
      // Plain text format - COMPLETE VERSION
      content = `CASE ANALYSIS REPORT\nGenerated: ${new Date().toLocaleString()}\n${'='.repeat(60)}\n\n`;
      
      if (shareSections.clientProfile && hasProfileData) {
        content += `👤 CLIENT PROFILE\n${'-'.repeat(40)}\n`;
        if (profileData.fullName || profileData.firstName) content += `Name: ${profileData.fullName || `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim()}\n`;
        if (profileData.email) content += `Email: ${profileData.email}\n`;
        if (profileData.phone) content += `Phone: ${profileData.phone}\n`;
        if (profileData.nationality) content += `Nationality: ${profileData.nationality}\n`;
        if (profileData.dateOfBirth) content += `Date of Birth: ${profileData.dateOfBirth}\n`;
        if (profileData.maritalStatus) content += `Marital Status: ${profileData.maritalStatus}\n`;
        if (profileData.occupation) content += `Occupation: ${profileData.occupation}\n`;
        if (profileData.employer) content += `Employer: ${profileData.employer}\n`;
        if (profileData.currentLocation) content += `Current Location: ${profileData.currentLocation}\n`;
        if (profileData.targetCountry) content += `Target Country: ${profileData.targetCountry}\n`;
        if (profileData.timeline) content += `Timeline: ${profileData.timeline}\n`;
        if (profileData.currentStatus) content += `Current Status: ${profileData.currentStatus}\n`;
        if (profileData.canEnglish) content += `English: ${profileData.canEnglish}\n`;
        if (profileData.canFrench) content += `French: ${profileData.canFrench}\n`;
        if (profileData.passportNumber) content += `Passport: ${profileData.passportNumber}\n`;
        if (profileData.passportExpiry) content += `Passport Expiry: ${profileData.passportExpiry}\n`;
        content += '\n';
      }
      
      if (shareSections.clientSummary && summary) {
        content += `📋 CLIENT SUMMARY\n${'-'.repeat(40)}\n`;
        if (summary.clientName) content += `Client: ${summary.clientName}\n`;
        if (summary.category) content += `Category: ${summary.category}\n`;
        if (summary.objective) content += `Objective: ${summary.objective}\n`;
        if (summary.context) content += `Context: ${summary.context}\n`;
        if (summary.nationality) content += `Nationality: ${summary.nationality}\n`;
        if (summary.currentStatus) content += `Current Status: ${summary.currentStatus}\n`;
        if (summary.currentLocation) content += `Location: ${summary.currentLocation}\n`;
        if (summary.targetCountry) content += `Target Country: ${summary.targetCountry}\n`;
        if (summary.timeline) content += `Timeline: ${summary.timeline}\n`;
        if (summary.keyFacts?.length > 0) {
          content += `\nKey Facts:\n${summary.keyFacts.map(f => `  • ${f}`).join('\n')}\n`;
        }
        content += '\n';
      }

      if (shareSections.assessment && assessment) {
        content += `📊 ASSESSMENT\n${'-'.repeat(40)}\n`;
        content += `Readiness Score: ${assessment.overallReadiness}%\n`;
        content += `Confidence Level: ${assessment.overallConfidence}\n`;
        if (assessment.statusIndicator) content += `Status: ${assessment.statusIndicator === 'green' ? 'Good Standing' : assessment.statusIndicator === 'yellow' ? 'Needs Attention' : 'Critical Issues'}\n`;
        if (assessment.complexityLevel) content += `Complexity: ${assessment.complexityLevel}\n`;
        if (assessment.priorityLevel) content += `Priority: ${assessment.priorityLevel}\n`;
        content += '\n';
      }

      if (shareSections.blockers && blockers?.length > 0) {
        content += `🚫 BLOCKERS (${blockers.length})\n${'-'.repeat(40)}\n`;
        content += blockers.map(b => `  ❌ ${b}`).join('\n') + '\n\n';
      }

      if (shareSections.risks && risks?.length > 0) {
        content += `⚠️ RISKS (${risks.length})\n${'-'.repeat(40)}\n`;
        content += risks.map(r => `  ⚠ ${r}`).join('\n') + '\n\n';
      }

      if (shareSections.pathways && pathways?.length > 0) {
        content += `🛤️ RECOMMENDED PATHWAYS (${pathways.length})\n${'-'.repeat(40)}\n`;
        pathways.forEach((p, i) => {
          content += `\n[${i + 1}] ${p.name} - Score: ${p.score}%\n`;
          if (p.description) content += `    Description: ${p.description}\n`;
          if (p.timeline) content += `    Timeline: ${p.timeline}\n`;
          if (p.estimatedCost) content += `    Est. Cost: ${p.estimatedCost}\n`;
          if (p.pros?.length > 0) content += `    Pros: ${p.pros.join(', ')}\n`;
          if (p.cons?.length > 0) content += `    Cons: ${p.cons.join(', ')}\n`;
          if (p.requirements?.length > 0) content += `    Requirements: ${p.requirements.join(', ')}\n`;
        });
        content += '\n';
      }

      if (shareSections.missingInfo && missingInformation?.length > 0) {
        content += `📋 MISSING INFORMATION (${missingInformation.length})\n${'-'.repeat(40)}\n`;
        missingInformation.forEach(m => {
          content += `  • [${m.priority?.toUpperCase()}] ${m.item}\n`;
          if (m.question) content += `    → Ask: "${m.question}"\n`;
        });
        content += '\n';
      }

      if (shareSections.nextSteps && nextSteps?.length > 0) {
        content += `✅ NEXT STEPS (${nextSteps.length})\n${'-'.repeat(40)}\n`;
        nextSteps.forEach((s, i) => {
          content += `  ${s.step || i + 1}. ${s.description}`;
          if (s.timeframe) content += ` (${s.timeframe})`;
          if (s.owner) content += ` - ${s.owner}`;
          content += '\n';
        });
        content += '\n';
      }

      if (shareSections.actionItems && actionItems?.length > 0) {
        content += `📌 ACTION ITEMS (${actionItems.length})\n${'-'.repeat(40)}\n`;
        actionItems.forEach(a => {
          content += `  • [${a.priority?.toUpperCase()}] ${a.action}`;
          if (a.deadline) content += ` - Due: ${a.deadline}`;
          content += '\n';
        });
        content += '\n';
      }

      if (shareSections.professionalAdvice && data.professionalAdvice) {
        const advice = data.professionalAdvice;
        content += `💼 PROFESSIONAL ADVICE\n${'-'.repeat(40)}\n`;
        if (advice.expertAnalysis) {
          content += `\nExpert Analysis:\n${advice.expertAnalysis}\n`;
        }
        if (advice.strategicRecommendations?.length > 0) {
          content += `\nStrategic Recommendations:\n`;
          advice.strategicRecommendations.forEach(r => {
            content += `  ★ ${r}\n`;
          });
        }
        if (advice.riskConsiderations) {
          content += `\nRisk Considerations:\n${advice.riskConsiderations}\n`;
        }
        if (advice.longTermPerspective) {
          content += `\nLong-term Perspective:\n${advice.longTermPerspective}\n`;
        }
        if (advice.alternativeApproaches?.length > 0) {
          content += `\nAlternative Approaches:\n`;
          advice.alternativeApproaches.forEach(a => {
            content += `  → ${a}\n`;
          });
        }
        content += '\n';
      }

      if (shareSections.clientCommunication && data.clientCommunication) {
        const comm = data.clientCommunication;
        content += `💬 CLIENT COMMUNICATION\n${'-'.repeat(40)}\n`;
        if (comm.suggestedFollowUp) {
          content += `\nSuggested Follow-up:\n${comm.suggestedFollowUp}\n`;
        }
        if (comm.documentsToRequest?.length > 0) {
          content += `\nDocuments to Request:\n`;
          comm.documentsToRequest.forEach(d => content += `  • ${d}\n`);
        }
        if (comm.questionsToAsk?.length > 0) {
          content += `\nQuestions to Ask:\n`;
          comm.questionsToAsk.forEach(q => content += `  • ${q}\n`);
        }
        content += '\n';
      }

      if (shareSections.longTermOutlook && data.longTermOutlook) {
        content += `🔮 LONG-TERM OUTLOOK\n${'-'.repeat(40)}\n`;
        content += `${data.longTermOutlook}\n\n`;
      }

      if (shareSections.firmCompliance && firmStandardsCompliance?.sections?.length > 0) {
        content += `🛡️ FIRM STANDARDS COMPLIANCE\n${'-'.repeat(40)}\n`;
        firmStandardsCompliance.sections.forEach(s => {
          const icon = s.status === 'compliant' ? '✓' : s.status === 'violation' ? '✗' : '⚠';
          content += `  ${icon} [${s.status?.toUpperCase()}] ${s.section}`;
          if (s.finding) content += ` - ${s.finding}`;
          content += '\n';
        });
        content += '\n';
      }

      if (shareSections.internalNotes && internalNotes?.length > 0) {
        content += `🔒 INTERNAL NOTES (CONFIDENTIAL)\n${'-'.repeat(40)}\n`;
        internalNotes.forEach(n => content += `  • ${n}\n`);
        content += '\n';
      }

      content += `${'='.repeat(60)}\nGenerated by Maven Platform | ${new Date().toLocaleString()}`;
    }
    
    return content;
};
